'use client'
import { Text } from '../text'
import { useMemo, ComponentPropsWithoutRef } from 'react'
import { Progress as RadixProgress } from 'radix-ui'
import { mtc, TypographyStyles } from '../../../utils/style-helpers'
import { rootStyles, indicatorStyles } from './styles'

interface ProgressProps
  extends ComponentPropsWithoutRef<typeof RadixProgress.Root> {
  className?: string
  value?: number
  /** Set the variant of the progress bar to one of: */
  variant?: 'primary' | 'segmented'
  size?: 'default' | 'large'
  numberOfSegments?: number
  activeSegment?: number
  segmentTitles?: string[]
}
// Need to generate unique keys for the progress segments
export const generateKeys = (count: number): string[] => {
  return Array.from({ length: count }, () =>
    Math.random().toString(36).slice(2)
  )
}

export const Progress = ({
  className,
  value = 0,
  variant = 'primary',
  size = 'default',
  numberOfSegments = 4,
  activeSegment,
  segmentTitles = [],
  ...props
}: ProgressProps) => {
  const keys = useMemo(() => generateKeys(numberOfSegments), [numberOfSegments])

  if (variant === 'segmented') {
    return (
      <div className={mtc('flex flex-row items-center gap-2', className)}>
        {Array.from({ length: numberOfSegments }).map((_, index) => {
          const translateClass =
            index < (activeSegment || 0) ? 'translate-x-0' : '-translate-x-full'
          return (
            <span key={keys[index]} className="w-full">
              <RadixProgress.Root
                key={keys[index]}
                className={mtc(rootStyles({ variant, size }), className)}
                {...props}
              >
                <RadixProgress.Indicator
                  key={keys[index]}
                  className={mtc(
                    indicatorStyles({ variant, size }),
                    translateClass
                  )}
                />
              </RadixProgress.Root>
              {segmentTitles[index] && (
                <Text
                  as={TypographyStyles.BODY_SM}
                  className={
                    index < (activeSegment || 0)
                      ? 'mt-2 text-moss-200'
                      : 'mt-2 text-neutral-400'
                  }
                >
                  {segmentTitles[index]}
                </Text>
              )}
            </span>
          )
        })}
      </div>
    )
  }

  return (
    <RadixProgress.Root
      className={mtc(rootStyles({ variant, size }), className)}
      {...props}
    >
      <RadixProgress.Indicator
        className={mtc(indicatorStyles({ variant, size }))}
        style={{ transform: `translateX(-${100 - (value || 0)}%)` }}
      />
    </RadixProgress.Root>
  )
}

Progress.displayName = 'Progress'
